/**
 * useWorldPresence — subscribe to presence for multiple worlds from the Dashboard.
 * Returns a map of worldId → online player info (count + names).
 * Read-only — does NOT track presence (we're not "in" these worlds).
 */

import { useEffect, useRef, useState } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

export interface WorldPresencePlayer {
  userName: string;
  avatarUrl: string;
}

export interface WorldPresenceInfo {
  count: number;
  players: WorldPresencePlayer[];
}

export function useWorldPresence(worldIds: string[]): Record<string, WorldPresenceInfo> {
  const [presenceMap, setPresenceMap] = useState<Record<string, WorldPresenceInfo>>({});
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());

  useEffect(() => {
    const channels = channelsRef.current;

    // Clean up channels for worlds no longer in the list
    for (const [wid, ch] of channels.entries()) {
      if (!worldIds.includes(wid)) {
        supabase.removeChannel(ch);
        channels.delete(wid);
      }
    }

    // Subscribe to new worlds
    for (const worldId of worldIds) {
      if (channels.has(worldId)) continue;

      try {
        const uniqueId = typeof crypto?.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        const ch = supabase.channel(`dashboard-presence:${worldId}`, {
          config: {
            presence: { key: `dashboard-${uniqueId}` },
          },
        });

        ch.on('presence', { event: 'sync' }, () => {
          const state = ch.presenceState();
          const players: WorldPresencePlayer[] = [];
          for (const presences of Object.values(state)) {
            const p = presences[0] as any;
            if (p) {
              players.push({ userName: p.userName || 'Player', avatarUrl: p.avatarUrl || '' });
            }
          }
          setPresenceMap((prev) => ({
            ...prev,
            [worldId]: { count: players.length, players },
          }));
        });

        ch.subscribe();
        channels.set(worldId, ch);
      } catch (err) {
        console.warn('[useWorldPresence] Failed to subscribe for world', worldId, err);
      }
    }

    return () => {
      for (const ch of channels.values()) {
        supabase.removeChannel(ch);
      }
      channels.clear();
    };
  }, [worldIds.join(',')]);

  return presenceMap;
}
